import { useState, useEffect, useCallback, useMemo, RefObject } from 'react';

type ElementInfo = {
  height: number | null;
  width: number | null;
  top: number | null;
  right: number | null;
  bottom: number | null;
  left: number | null;
};

type WindowInfo = {
  width: number | null;
  height: number | null;
};

function useViewPortPosition(ref: RefObject<HTMLElement>) {
  const [elementInfo, setElementInfo] = useState<ElementInfo>({
    height: null,
    width: null,
    top: null,
    right: null,
    bottom: null,
    left: null,
  });

  const [windowInfo, setWindowInfo] = useState<WindowInfo>({
    width: null,
    height: null,
  });

  const handleScroll = useCallback(() => {
    if (!ref.current) return;

    const { bottom, height, left, right, top, width } =
      ref.current.getBoundingClientRect();

    setElementInfo({
      height,
      width,
      top,
      right,
      bottom,
      left,
    });
  }, []);

  const handleResize = useCallback(() => {
    setWindowInfo({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  const additionalInfo = useMemo(() => {
    if (
      windowInfo.height &&
      elementInfo.top &&
      windowInfo.width &&
      elementInfo.left
    ) {
      return {
        isOnFirstVerticalHalf: elementInfo.top <= windowInfo.height / 2,
        isOnFirstHorizontalHalf: elementInfo.left <= windowInfo.width / 2,
      };
    }
  }, [elementInfo.top, elementInfo.left, windowInfo.height, windowInfo.width]);

  useEffect(() => {
    handleScroll();
    handleResize();

    document.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    ...elementInfo,
    ...additionalInfo,
  };
}

export default useViewPortPosition;
